import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getWebsiteSettings } from '../utils/websiteSettings';
import { getImageUrl } from '../utils/imageUtils';

const WebsiteSettingsContext = createContext(null);

export const useWebsiteSettings = () => {
  const ctx = useContext(WebsiteSettingsContext);
  return ctx || { settings: null, theme: {}, loading: true, error: null, getImageUrl };
};

const DEFAULT_THEME = {
  primaryColor: '#2563eb',
  secondaryColor: '#1e40af',
  accentColor: '#f59e0b'
};

const DEFAULT_NAV = [
  { label: 'Flights', url: '/flights', icon: 'flight' },
  { label: 'Trains', url: '/trains', icon: 'train' },
  { label: 'Hotels', url: '/hotels', icon: 'hotel' },
  { label: 'Restaurant', url: '/restaurants', icon: 'restaurant' },
  { label: 'Buses', url: '/buses', icon: 'bus' },
  { label: 'Car', url: '/transfers', icon: 'car' },
  { label: 'Holidays', url: '/holidays', icon: 'holiday' }
];

export const WebsiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getWebsiteSettings();
        if (!cancelled && data) setSettings(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load website settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const theme = useMemo(() => ({
    ...DEFAULT_THEME,
    ...(settings?.theme || {})
  }), [settings?.theme]);

  const value = useMemo(() => ({
    settings,
    theme,
    loading,
    error,
    getImageUrl,
    header: settings?.header || {},
    footer: settings?.footer || {},
    nav: settings?.header?.navigation?.length ? settings.header.navigation : DEFAULT_NAV,
    showSearch: settings?.header?.showSearch !== false,
    showUserMenu: settings?.header?.showUserMenu !== false
  }), [settings, theme, loading, error]);

  return (
    <WebsiteSettingsContext.Provider value={value}>
      {children}
    </WebsiteSettingsContext.Provider>
  );
};

export default WebsiteSettingsContext;
